
import { isEmpty, find, clone } from 'lodash';
import Driver from 'driver.js';
import { dom } from 'quasar';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import moment from 'moment';
import ActivitiesList from '../views/budgets/ActivitiesList.vue';
import NotAuthorized from '../views/budgets/NotAuthorized.vue';
import AdsApi from '../libs/ads-request/ads-api';
import { addActivity, removeDuplicatedPackages } from '../libs/helpers';
import vwgif from '../assets/vw-toggle.gif';
import audigif from '../assets/audi-toggle.gif';
import seatgif from '../assets/seat-toggle.gif';
import QuotationAdapter from '../libs/adapters/Quotation.adapter';
import QuotationRules from '../libs/business-logic/Quotation.rules';
import ActivitiesRules from '../libs/business-logic/Activities.rules';
import getAdditionalQuotationAmounts from '../modules/quotation/application/getAdditionalQuotationAmounts';
import createApiQuotationRepository from '../modules/quotation/infrastructure/QuotationApiRepository';
import AQuotationAmount from './AQuotationAmount.vue';
import ServiceTotalAmount from '../modules/quotation/domain/ServiceTotalAmount';

const BUDGET_STATUS = [103, 104, 105, 107];

export default {
  name: 'ChecklistComponent',
  components: {
    ActivitiesList,
    NotAuthorized,
    AQuotationAmount,
  },
  props: {
    reset: Boolean,
    contracted: Boolean,
    shadow: Boolean,
    unbudgetList: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    redList: [],
    yellowList: [],
    greenList: [],
    isAuthGeneralBudget: false,
    isAcceptBudget: false,
    isAuthorized: false,
    isRejectedPoints: false,
    additionalQuotationSubtotal: 0,
    additionalQuotationTaxes: 0,
    taxesAmountPreinvoice: 0,
    preInvoiceTotal: 0,
    preInvoiceSubtotal: 0,
    preInvoiceTotalAltern: 0,
    total: 0,
    additionalQuotationTotal: 0,
    showTooltip: false,
    activitySelected: null,
    authorizedDisable: false,
    heightImage: 0,
    isFinishTutorial: false,
    tutorialActive: false,
    expand: false,
    expandTotals: false,
    showTotalMobile: false,
    showButtonCloseTutorial: false,
    driver: null,
    responseComplete: [],
    showTutorialMobile: false,
    stepTutorialMobile: 0,
    titleTutorialMobile: '',
    textTutorialMobile: '',
    showView: false,
    showTotals: true,
    loaderMessage: 'Cargando presupuestos adicionales ...',
    loaderError: false,
    effectiveDate: null,
    effectiveDateEnd: null,
    recordsWithBudget: 0,
    showList: 1,
    otherCharges: 0,
    addOtherCharges: false,
    titleAdditionals: '¡Revisa tu reporte de reparaciones adicionales!',
    subAdditionals: 'Aquí podrás ver las reparaciones adicionales que necesita tu vehículo',
    groupedQuotation: null,
  }),
  computed: {
    ...mapGetters('Quotation', {
      otherChargesSubtotal: 'getOtherChargesSubtotal',
      quotationItems: 'getQuotationItems',
      rejectedActivities: 'getRejectedActivities',
    }),
    ...mapGetters('Preinvoice', {
      initialQuotationAmounts: 'getInitialQuotationAmounts',
    }),
    ...mapGetters('Tutorial', {
      activateTutorial: 'getActivateTutorial',
    }),
    ...mapGetters('Activity', {
      additionalOrdersGreenActivities: 'getAdditionalOrdersGreenActivities',
      parentSuperorderGreenAdditionalActivities: 'parentSuperorderGreenAdditionalActivities',
    }),

    desktop() {
      return this.$q.screen.gt.md;
    },
    tablet() {
      return this.$q.screen.lt.lg && this.$q.screen.gt.sm;
    },
    mobile() {
      return this.$q.screen.lt.md;
    },
    isThemeDefault() {
      return this.$session.get('brand') === '';
    },
    isThemeVolkswagen() {
      return this.$session.get('brand') === 'volkswagen';
    },
    isThemeAudi() {
      return this.$session.get('brand') === 'audi';
    },
    isInternal() {
      return this.$session.get('isInternal');
    },

    /**
     * Retrieves the total service amount.
     *
     * @returns {string} The total service amount.
     */
    serviceTotal() {
      return new ServiceTotalAmount(
        this.initialQuotationAmounts.total,
        this.additionalQuotationTotal,
      ).getValue();
    },

    activities() {
      if (this.quotationItems) {
        const groupedQuotationItems = QuotationRules.groupQuotationItemsByActivity(
          this.quotationItems,
        );
        return ActivitiesRules.getActivitiesFromGroupedQuotationItems(groupedQuotationItems);
      }

      return [];
    },

    redActivities() {
      return ActivitiesRules.getRedActivities(this.activities, this.unbudgetList);
    },

    yellowActivities() {
      return ActivitiesRules.getYellowActivities(this.activities, this.unbudgetList);
    },

    greenActivities() {
      const greenActivitiesFromParentOrder = ActivitiesRules.greenActivities(this.unbudgetList);

      return [
        ...greenActivitiesFromParentOrder.concat(this.additionalOrdersGreenActivities),
        ...this.parentSuperorderGreenAdditionalActivities,
      ];
    },

    offerStartDate() {
      if (this.$session.get('effectiveDate')) {
        return moment(this.$session.get('effectiveDate')).format('DD [de] MMMM [de] YYYY');
      }
      return '--';
    },

    offerEndDate() {
      if (this.$session.get('effectiveDate')) {
        const effectiveDate = moment(this.$session.get('effectiveDateEnd'));
        const lastDayOfEffectiveDate = effectiveDate.endOf('month');
        return moment(lastDayOfEffectiveDate).format('DD [de] MMMM [de] YYYY');
      }
      return '--';
    },

    someClientChargedActivityHasToggleBlocked() {
      return this.clientChargedActivities.some(
        (activity) => activity.view_status === '103' || activity.view_status === '105',
      );
    },

    clientChargedActivities() {
      const activities = this.redActivities.concat(this.yellowActivities);
      return ActivitiesRules.getClientChargedActivities(activities);
    },
  },
  watch: {
    reset() {
      this.activitySelected = null;
    },
    activateTutorial() {
      if (this.activateTutorial) {
        this.showTooltip = false;
        this.showButtonCloseTutorial = true;
        if (!this.mobile) this.initTour();
        else this.initTutorialMobile();
      } else {
        this.showTooltip = true;
        this.tutorialActive = false;
      }
    },
    tutorialActive(val) {
      if (val) {
        this.$refs.activityTotal.$el.classList.remove('sticky');
      }
    },
    showTutorialMobile(status) {
      this.$emit('showTutorialMobile', status);
    },

    redList: {
      deep: true,
      inmediate: true,
      handler(newRedList) {
        if (
          newRedList.every((activity) => activity.accepted != null) === true
          && this.yellowList.every((activity) => activity.accepted != null) === true
        ) {
          this.authorizedDisable = false;
        } else {
          this.authorizedDisable = true;
        }
      },
    },
    yellowList: {
      deep: true,
      inmediate: true,
      handler(newYellowList) {
        if (
          this.redList.every((activity) => activity.accepted != null) === true
          && newYellowList.every((activity) => activity.accepted != null) === true
        ) {
          this.authorizedDisable = false;
        } else {
          this.authorizedDisable = true;
        }
      },
    },
  },
  async mounted() {
    this.SET_ACTIVATE_TUTORIAL(false);
    await this.initialCharge();
    this.SET_ACTIVITIES_GROUPED_BY_ID(this.activitiesGroup());
  },
  methods: {
    ...mapActions('Quotation', ['getFrozenQuotation', 'freezeQuotation', 'getOriginalQuotation']),
    ...mapMutations('Preinvoice', ['SET_PREINVOICE']),
    ...mapMutations('Tutorial', ['SET_ACTIVATE_TUTORIAL']),
    ...mapMutations('Quotation', ['UPDATE_AUTHORIZATION_SELECTION']),
    ...mapMutations('Activity', ['SET_ACTIVITIES_GROUPED_BY_ID', 'SET_ADDITIONAL_ORDERS_ACTIVITIES']),

    isEmpty,
    afterCharge() {
      if (!this.mobile) {
        const { height } = dom;
        this.heightImage = height(document.getElementById('container-image'));
      }

      if ((this.$session.get('showTutorial') === 0 || this.$session.get('showTutorial') === null) && (this.redList.length > 0 || this.yellowList.length > 0)
          && (this.redList[0].show === true || this.yellowList[0].show === true)) {
        setTimeout(() => {
          if (!this.mobile) {
            this.initTour();
          } else {
            this.initTutorialMobile();
          }
        }, 500);
      }
      if (this.redList.length > 0 && !this.mobile) {
        this.$refs.redActivities.$refs.activity[0].click();
      } else if (this.yellowList.length > 0 && !this.mobile) {
        this.$refs.yellowActivities.$refs.activity[0].click();
      }
    },
    authorizeOrder() {
      this.loaderShow('Autorizando orden');

      const packages = this.getMappedItemsForAcceptQuotation();
      AdsApi.acceptQuotation(packages)
        .then(async (res) => {
          await this.freezeQuotation();
          const response = res.data;
          if (!response.success) throw new Error(`[${response.errorCode}] ${response.errorDescription}`);
          this.sendPushAdviser();
          this.$session.remove('activities');
          this.disabled = true;
          this.isAuthorized = true;
          this.isAuthGeneralBudget = false;
          this.redList = [];
          this.yellowList = [];
          this.greenList = [];
          if (this.isAuthorized) {
            this.isFinishTutorial = true;
            this.showTooltip = false;
          }
          await this.getPreinvoice();
          await this.getAdditionalActivities();
          await this.getUnbudgetedActivities();
          this.$nextTick(() => {
            this.isAcceptBudget = true;
            setTimeout(() => {
              this.isAcceptBudget = false;
            }, 3000);
          });
        })
        .catch((error) => this.alertShow(error, 'error'))
        .finally(this.loaderHide);
    },
    async changeItem(item) {
      const clonedItem = item;
      if (this.authorizedDisable) this.authorizedDisable = false;
      clonedItem.accepted = !clonedItem.accepted;

      this.checkListActivities();
      this.UPDATE_AUTHORIZATION_SELECTION({
        activityId: clonedItem.order_activity_id,
        acceptedValue: item.accepted,
      });

      try {
        await this.reviewQuoation();
        if (this.addOtherCharges) {
          this.total = this.Decimal(this.additionalQuotationSubtotal)
            .add(this.Decimal(this.additionalQuotationTaxes))
            .add(this.Decimal(this.preInvoiceTotal))
            .add(this.otherChargesSubtotal)
            .toFixed(2)
            .toString();
        } else {
          this.total = this.Decimal(this.additionalQuotationSubtotal)
            .add(this.Decimal(this.additionalQuotationTaxes))
            .add(this.Decimal(this.preInvoiceTotal))
            .toFixed(2)
            .toString();
        }
      } catch (error) {
        this.alertShow(error, 'error');
      }
      this.setActivitiesInSession();
    },
    disabledActivity() {
      const activitiesInPack = [];

      const dataRed = this.checkActivityDisabled(this.redList, activitiesInPack);
      this.redList = dataRed.list;

      const dataYellow = this.checkActivityDisabled(this.yellowList, dataRed.activitiesInPack);
      this.yellowList = dataYellow.list;
    },
    checkActivityDisabled(list, activitiesInPack) {
      list.forEach((activity) => {
        const clonedActivity = activity;
        if (!this.isAuthorized) {
          if (clonedActivity.pack_category !== 'CP') {
            const object = find(activitiesInPack, (a) => a.pack_id === clonedActivity.pack_id);
            if (object == null) activitiesInPack.push(clonedActivity);
            clonedActivity.disabled = this.checkDisableElement(activitiesInPack, clonedActivity);
          } else {
            clonedActivity.disabled = false;
          }
        } else {
          clonedActivity.disabled = true;
        }
      });

      return {
        list,
        activitiesInPack,
      };
    },
    checkDisableElement(activitiesInPack, clonedActivity) {
      let disabled = false;
      if (activitiesInPack.length > 0) {
        for (let index = 0; index < activitiesInPack.length; index += 1) {
          const act = activitiesInPack[index];
          if (act.order_activity_id !== clonedActivity.order_activity_id
                  && act.pack_id === clonedActivity.pack_id) {
            disabled = true;
          }
        }
      }
      return disabled;
    },
    async getAdditionalActivities() {
      return this.getFrozenQuotation()
        .then(async (data) => {
          await this.getOriginalQuotation();
          this.responseComplete = data;
          this.activitiesRemapMethod(data);

          this.disabledActivity();

          if (!this.$session.get('activities')) this.setActivitiesInSession();
          else this.updateActivitiesInSesion();

          const activities = this.$session.get('activities');
          const red = activities.redList;
          const yellow = activities.yellowList;

          let validate = false;

          if (this.redList.length > 0) {
            this.redList.forEach((activity) => {
              const clonedActivity = activity;
              const act = find(
                red,
                (a) => a.order_activity_id === clonedActivity.order_activity_id,
              );
              if (act != null) clonedActivity.accepted = act.accepted;
            });
            const found = this.redList.filter((activity) => activity.accepted !== null);
            if (found.length > 0) validate = true;
          }

          if (this.yellowList.length > 0) {
            this.yellowList.forEach((activity) => {
              const clonedActivity = activity;
              const act = find(
                yellow,
                (a) => a.order_activity_id === clonedActivity.order_activity_id,
              );
              if (act != null) clonedActivity.accepted = act.accepted;
            });
            const found = this.yellowList.filter((activity) => activity.accepted !== null);
            if (found.length > 0) validate = true;
          }
          if (!validate) this.authorizedDisable = true;
        })
        .catch((error) => {
          throw error;
        });
    },
    activitiesRemapMethod(data) {
      let authorized = true;
      let activitiesAcepted = 0;
      const packIds = [];
      data.forEach((activity) => {
        const clonedActivity = activity;
        let acceptedActivity = null;

        if (!BUDGET_STATUS.includes(+clonedActivity.view_status)
              && authorized === true) authorized = false;

        if (+clonedActivity.view_status === 107) activitiesAcepted += 1;

        clonedActivity.show = true;

        if (clonedActivity.accepted === 0) {
          acceptedActivity = false;
        } else if (clonedActivity.accepted === 1) {
          acceptedActivity = true;
        }

        const accepted = acceptedActivity;

        clonedActivity.accepted = accepted;

        if (clonedActivity.activity_status_id === 3) {
          this.yellowList = addActivity(clonedActivity, this.yellowList, packIds);
        }
        if (clonedActivity.activity_status_id === 4) {
          this.redList = addActivity(clonedActivity, this.redList, packIds);
        }
        if (+clonedActivity.otherChargesAmount > 0 && clonedActivity.activity_service_type !== 2) {
          this.otherCharges = clonedActivity.material_cost ?? 0;
        }
      });

      this.isAuthorized = authorized;

      this.showTotals = !(activitiesAcepted === data.length);

      this.recordsWithBudget = this.redList.length + this.yellowList.length;
    },
    getUnbudgetedActivities() {
      return AdsApi.getUnbudgetedActivities()
        .then((orders) => {
          this.SET_ADDITIONAL_ORDERS_ACTIVITIES(orders);
          orders.forEach((order) => {
            this.orderRemap(order);
          });

          this.redList = this.sortList(this.redList);
          this.yellowList = this.sortList(this.yellowList);
        })
        .catch((error) => {
          throw error;
        });
    },
    orderRemap(order) {
      order.activityClient.forEach((activity) => {
        const principalActivityCloned = activity;
        principalActivityCloned.show = false;

        if (
          principalActivityCloned.activity_status_id === 2
          && principalActivityCloned.inventario === 0
        ) {
          const object = find(
            this.greenList,
            (a) => a.order_activity_id === principalActivityCloned.order_activity_id,
          );
          if (object == null) this.greenList.push(principalActivityCloned);
        }

        if (principalActivityCloned.activity_status_id === 3
                && principalActivityCloned.pack_id_dms != null) {
          const object = find(
            this.yellowList,
            (a) => a.order_activity_id === principalActivityCloned.order_activity_id,
          );
          if (object == null) this.yellowList.push(principalActivityCloned);
        }

        if (principalActivityCloned.activity_status_id === 4
                && principalActivityCloned.pack_id_dms != null) {
          const object = find(
            this.redList,
            (a) => a.order_activity_id === principalActivityCloned.order_activity_id,
          );
          if (object == null) this.redList.push(principalActivityCloned);
        }
      });
    },
    getPreinvoice() {
      return AdsApi.getPreinvoice()
        .then((data) => {
          this.SET_PREINVOICE(data);
          this.preInvoiceTotal = data.total;
          this.preInvoiceSubtotal = data.subtotal;
          this.taxesAmountPreinvoice = data.taxesAmount;
          this.preInvoiceTotalAltern = data.total;
        })
        .catch((error) => {
          throw error;
        });
    },
    redirectToReport(activity) {
      this.activitySelected = activity.order_activity_id;

      if (!this.mobile) {
        this.$emit('showDetail', {
          activity,
          heightImage: this.heightImage,
        });
      } else {
        this.$router.push({
          name: 'report',
          params: {
            id: activity.order_activity_id,
            activity,
          },
        });
      }
    },
    scrollHandler() {
      const { activitiesContainer } = this.$refs;
      const activitiesContainerOffset = activitiesContainer.$el.offsetTop + 500;
      const { activityTotal } = this.$refs;
      const offset = this.$q.screen.height + window.pageYOffset;

      if (!this.tutorialActive) {
        if (offset > activitiesContainerOffset) {
          activityTotal.$el.classList.add('sticky');
        } else {
          activityTotal.$el.classList.remove('sticky');
        }
      }
    },
    sendPushAdviser() {
      AdsApi.sendPushAdviser()
        .then((res) => {
          const response = res.data;

          if (!response.success) throw new Error(`[${response.errorCode}] ${response.errorDescription}`);
        })
        .catch((err) => this.alertShow(err, 'error'));
    },
    initTour() {
      this.$nextTick(() => {
        this.driver = null;
        const tutorialElementClass = 'tutorial-authorization';
        const tutotialTopPosition = 'top-center';
        let image = null;

        switch (this.$session.get('brand')) {
          case 'volkswagen':
            image = vwgif;
            break;
          case 'audi':
            image = audigif;
            break;
          case 'seat':
            image = seatgif;
            break;
          default:
            break;
        }
        this.driver = new Driver({
          className: 'tutorial',
          padding: 1,
          doneBtnText: 'Siguiente >',
          closeBtnText: 'Cerrar',
          nextBtnText: 'Siguiente >',
          prevBtnText: '< Anterior',
          opacity: 0.2,
          allowClose: false,
          onDeselected: () => {
            this.showButtonCloseTutorial = false;
          },
          onHighlighted: (element) => {
            this.showButtonCloseTutorial = true;
            this.tutorialActive = true;

            if (element.getNode().id === 'activity-authorization') {
              setTimeout(() => {
                const popover = document.getElementsByClassName(tutorialElementClass).item(0);
                const top = parseInt(popover.style.top, 10);
                const left = parseInt(popover.style.left, 10);
                popover.style.top = `${top - 15}px`;
                popover.style.left = `${left + 15}px`;
              }, 300);
            }

            if (element.getNode().id === 'activity-restrictions') {
              setTimeout(() => {
                const popover = document.getElementsByClassName('tutorial-restriccions').item(0);
                const top = parseInt(popover.style.top, 10);
                const left = parseInt(popover.style.left, 10);
                popover.style.top = `${top - 15}px`;
                popover.style.left = `${left + 15}px`;
              }, 300);
            }
          },
        });

        let position = tutotialTopPosition;

        if (this.tablet || this.mobile) {
          position = 'top-right';
        }
        const steps = [
          {
            element: '#activities-container',
            popover: {
              title: this.titleAdditionals,
              description: this.subAdditionals,
              position: tutotialTopPosition,
              className: 'activities-container',
            },
          },
          {
            element: '#activity-authorization',
            popover: {
              title: '¡Repara los detalles!',
              description: `<div>Aquí podrás autorizar o rechazar las averías</div><div style='height: 57px; width: 340px;'><img src='${image}' alt='toggle' /></div>`,
              position,
              className: tutorialElementClass,
            },
          },
        ];

        if (this.checkRestriccions()) {
          steps.push({
            element: '#activity-restrictions',
            popover: {
              title: 'Checa las restricciones del paquete',
              description: 'Da clic en la etiqueta.',
              position: tutotialTopPosition,
              className: 'tutorial-restriccions',
            },
          });
        }
        steps.push({
          element: '#activity-total',
          popover: {
            title: '¡Autoriza tu presupuesto aquí!',
            description:
              'Autoriza tus reparaciones y enseguida comenzaremos a trabajar, si tienes dudas, podrás contactar a tu asesor de servicio para aclararlas',
            position: 'top-right',
            className: tutorialElementClass,
          },
          onNext: () => {
            this.isFinishTutorial = true;
          },
        });

        this.driver.defineSteps(steps);

        this.driver.start();
      });
    },
    async initialCharge() {
      try {
        await this.getPreinvoice();
        await this.getAdditionalActivities();
        await this.getUnbudgetedActivities();
        await this.reviewQuoation(false);
        if (this.showTotals) {
          this.total = this.Decimal(this.additionalQuotationSubtotal)
            .add(this.Decimal(this.additionalQuotationTaxes))
            .add(this.Decimal(this.preInvoiceTotal))
            .toFixed(2)
            .toString();
        } else {
          this.total = this.Decimal(this.preInvoiceTotal)
            .toFixed(2)
            .toString();
        }

        if (this.addOtherCharges) {
          this.total = this.Decimal(this.total)
            .add(this.additionalQuotationSubtotal !== 0 ? this.otherChargesSubtotal : 0)
            .toFixed(2)
            .toString();
        }
        this.showView = true;
      } catch (error) {
        this.alertShow(error, 'error');
        this.loaderError = true;
      }
    },
    initTutorialMobile() {
      this.showTutorialMobile = true;
      this.tutorialActive = true;
      this.showButtonCloseTutorial = true;
    },
    nextStep() {
      let next = null;
      this.stepTutorialMobile += 1;
      if (this.stepTutorialMobile === 3) next = 1;
      this.changeTutorialMobile(next);
    },
    previousStep() {
      let previous = null;
      this.stepTutorialMobile -= 1;
      if (this.stepTutorialMobile === 3) previous = 0;
      this.changeTutorialMobile(previous);
    },
    changeTutorialMobile(ban) {
      let image = null;

      switch (this.$session.get('brand')) {
        case 'volkswagen':
          image = vwgif;
          break;
        case 'audi':
          image = audigif;
          break;
        case 'seat':
          image = seatgif;
          break;
        default:
          break;
      }

      switch (this.stepTutorialMobile) {
        case 0:
          this.titleTutorialMobile = this.titleAdditionals;
          this.textTutorialMobile = this.subAdditionals;
          break;
        case 1:
          this.titleTutorialMobile = 'Checa los detalles de cada avería';
          this.textTutorialMobile = 'Da clic en el ícono y revisa los detalles';
          break;
        case 2:
          this.titleTutorialMobile = '¡Repara los detalles!';
          this.textTutorialMobile = `<div>Aquí podrás autorizar o rechazar las averías</div><div class='full-width text-center' style='height: 57px; width: 340px;'><img src='${image}' alt='toggle' /></div>`;
          break;
        case 3:
          if (this.checkRestriccions()) {
            this.titleTutorialMobile = 'Checa las restricciones del paquete';
            this.textTutorialMobile = 'Da clic en la etiqueta';
          } else {
            if (ban === 1) this.nextStep();
            if (ban === 0) this.previousStep();
          }
          break;
        case 4:
          window.scrollTo(0, this.$refs.activityTotal.$el.offsetHeight + 254);
          this.titleTutorialMobile = '¡Autoriza tu presupuesto aquí!';
          this.textTutorialMobile = 'Autoriza tus reparaciones y enseguida comenzaremos a trabajar, si tienes dudas, podrás contactar a tu asesor de servicio para aclararlas';
          break;
        case 5:
          this.showButtonCloseTutorial = false;
          this.showTotalMobile = false;
          this.isFinishTutorial = true;
          this.titleTutorialMobile = this.titleAdditionals;
          this.textTutorialMobile = this.subAdditionals;
          break;
        default:
          break;
      }
    },
    resetTutorial(type) {
      if (this.driver != null) this.driver.reset();

      if (type === 'close') {
        this.isFinishTutorial = false;
        this.showTooltip = true;
      }

      this.driver = null;
      this.tutorialActive = false;
      this.showButtonCloseTutorial = false;
      this.showTotalMobile = false;
      this.showTutorialMobile = false;
      this.stepTutorialMobile = 0;
      this.SET_ACTIVATE_TUTORIAL(false);
      this.updateTutorial();
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 500);
    },
    async reviewQuoation(loader = true) {
      const packIds = [];

      if (loader) this.loaderShow('Calculando totales');

      this.responseComplete.forEach((activity) => {
        const clonedActivity = activity;
        let found = null;
        found = find(this.redList, (a) => activity.order_activity_id === a.order_activity_id);
        if (found != null) clonedActivity.accepted = found.accepted;
        found = find(this.yellowList, (a) => activity.order_activity_id === a.order_activity_id);
        if (found != null) clonedActivity.accepted = found.accepted;
      });

      let details = this.responseComplete
        .filter((activity) => {
          const exist = find(packIds, (p) => activity.pack_id === p);
          if (exist === null && activity.pack_category !== 'CP') packIds.push(activity.pack_id);
          return (
            activity.accepted === true
            && exist == null
            && +activity.view_status !== 107
            && activity.activity_service_type !== 2
            && activity.activity_service_type !== null
          );
        })
        .map((activity) => {
          const object = {
            order_activity_id: activity.order_activity_id,
            description: activity.description,
            title: activity.title,
            activity_status_id: activity.activity_status_id,
            pack_id: activity.pack_id,
            pack_id_dms: activity.pack_id_dms,
            pack_desc: activity.pack_desc,
            pack_price: activity.pack_price,
            part_desc: activity.part_desc,
            pack_category: activity.pack_category,
            quotation_id: activity.quotation_id,
            accepted: 1,
            timeUnits: +activity.part_quantity,
          };

          if (activity.part_brand !== 'manoDeObra') {
            object.part_id = activity.part_id;
            object.part_id_dms = activity.part_id_dms;
          } else {
            object.labours_id = activity.part_id;
            object.labours_id_dms = activity.part_id_dms;
          }

          if (activity.pack_id_dms == null) object.part_quantity = activity.part_quantity;

          return object;
        });

      details = removeDuplicatedPackages(details);
      await this.reviewQuotationRequest(details, loader);
    },
    async reviewQuotationRequest(details, loader) {
      const acceptedActivities = QuotationRules.getAcceptedActivities(this.quotationItems);

      const reviewQuotationDetails = QuotationAdapter.getReviewQuotationDetailsDto(
        acceptedActivities,
        this.quotationItems,
      );

      const payload = [];

      if (acceptedActivities.length !== 0) {
        reviewQuotationDetails.forEach((item) => {
          const existPackageInPayload = payload.some(
            (payloadItem) => payloadItem.pack_id_dms === item.pack_id_dms,
          );

          if (!existPackageInPayload || !item.pack_id_dms) {
            payload.push(item);
          }
        });
      }

      const additionalQuotationAmounts = await getAdditionalQuotationAmounts(
        createApiQuotationRepository(),
        payload,
      );

      this.additionalQuotationTaxes = additionalQuotationAmounts.taxes;
      this.additionalQuotationSubtotal = additionalQuotationAmounts.subtotal;
      this.additionalQuotationTotal = additionalQuotationAmounts.total;

      if (loader) this.loaderHide();
    },
    setActivitiesInSession() {
      const activities = {
        redList: clone(this.redList),
        yellowList: clone(this.yellowList),
      };

      this.$session.set('activities', activities);
    },
    updateTutorial() {
      if (this.$session.get('showTutorial') === 0 || this.$session.get('showTutorial') === null) {
        this.$session.set('showTutorial', true);
        AdsApi.updateTutorial()
          .then((res) => {
            const responseData = res.data;

            if (!responseData.success) throw new Error(`[${responseData.errorCode}] ${responseData.errorDescription}`);
          })
          .catch((err) => this.alertShow(err, 'error'));
      }
    },
    validateBudget() {
      if (this.rejectedActivities.length > 0) {
        this.isRejectedPoints = true;
      } else {
        this.isAuthGeneralBudget = true;
      }
    },
    updateActivitiesInSesion() {
      const activities = this.$session.get('activities');
      const redData = this.checkAcceptedInArrray(activities.redList, this.redList);
      const yellowData = this.checkAcceptedInArrray(activities.yellowList, this.yellowList);

      const data = {
        redList: redData,
        yellowList: yellowData,
      };
      this.$session.set('activities', data);
    },
    checkAcceptedInArrray(oldArray, newArray) {
      const data = [];
      newArray.forEach((item) => {
        const clonedItem = item;
        const activity = find(
          oldArray,
          (a) => a.order_activity_id === clonedItem.order_activity_id,
        );
        if (activity && Object.prototype.hasOwnProperty.call(activity, 'accepted') && !this.isAuthorized) { clonedItem.accepted = activity.accepted; }

        data.push({ ...item });
      });
      return data;
    },
    sortList(list) {
      return list.sort((a) => {
        if (a.evidence === 1) {
          return -1;
        }
        return 1;
      });
    },
    checkRestriccions() {
      let restrictions = false;
      if (this.redList && this.redList.length > 0) {
        if (
          this.redList[0].restrictions
          && this.redList[0].restrictions !== null
          && this.redList[0].restrictions.length > 0
        ) {
          restrictions = true;
        } else {
          restrictions = false;
        }
      } else {
        restrictions = false;
      }
      return restrictions;
    },
    checkListActivities() {
      if (
        this.redList
          .findIndex((activity) => activity.accepted
          && activity.activity_service_type === 1) >= 0
        || this.yellowList
          .findIndex((activity) => activity.accepted
          && activity.activity_service_type === 1) >= 0
      ) {
        this.addOtherCharges = true;
      } else {
        this.addOtherCharges = false;
      }
    },
    getMappedItemsForAcceptQuotation() {
      const acceptedActivities = QuotationRules.getAcceptedActivities(this.quotationItems);
      const mappedItems = [];
      const quotationItemsWithAcceptProps = this.quotationItems.map((quotationItem) => {
        const item = {
          quotationId: null,
          status: 0,
          packageId: null,
          partId: null,
          packageCategory: null,
          labourId: null,
        };
        item.quotationId = quotationItem.quotation_id;

        if (quotationItem.activity_service_type === 2) {
          item.status = 1;
        } else if (acceptedActivities.includes(quotationItem.order_activity_id)) {
          item.status = 1;
        } else {
          item.status = 0;
        }

        if (QuotationRules.isPpsoItem(quotationItem)
          || QuotationRules.isAgencyPackageItem(quotationItem)) {
          item.packageId = quotationItem.pack_id;
        }

        if (QuotationRules.isSparepart(quotationItem)) {
          item.partId = quotationItem.part_id;
        }

        if (QuotationRules.isLabour(quotationItem)) {
          item.labourId = quotationItem.part_id;
        }

        if (QuotationRules.isOtherCharge(quotationItem)) {
          item.packageId = null;
          item.packageCategory = null;
        }

        return item;
      });

      quotationItemsWithAcceptProps.forEach((quotationItem) => {
        if (!quotationItem.packageId) {
          mappedItems.push(quotationItem);
        }

        if (!mappedItems.some(
          (item) => item.packageId === quotationItem.packageId,
        )
        ) {
          mappedItems.push(quotationItem);
        }
      });

      return mappedItems;
    },
    activitiesGroup() {
      if (this.quotationItems) {
        return QuotationRules.groupQuotationItemsByActivity(
          this.quotationItems,
        );
      }
      return [];
    },
  },
};
